import React, { useState, useEffect, useCallback } from "react";

import { useLocalization } from "@fluent/react";
import { Button, Chip, Radio, Row, TextInput } from "@narmi/design_system";
import Dialog from "cerulean/src/Dialog";

import {
  generateDefaultFileName,
  validateFileName,
  updateFileExtension,
} from "../../utils/filenameUtils";

import styles from "./ExportDialog.module.scss";

import type { ExportFormat } from "../../constants";
import type { FileNameErrorKey } from "../../utils/filenameUtils";

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFormat: ExportFormat;
  onFormatChange: (format: ExportFormat) => void;
  onDownload: (fileName: string) => void;
  savedViewName?: string;
  isExporting: boolean;
}

const FILE_NAME_ERROR_KEYS: Record<FileNameErrorKey, string> = {
  required: "transaction-export-filename-error-required",
  "invalid-chars": "transaction-export-filename-error-invalid-chars",
  "too-long": "transaction-export-filename-error-too-long",
};

const ExportDialog = ({
  isOpen,
  onClose,
  selectedFormat,
  onFormatChange,
  onDownload,
  savedViewName,
  isExporting,
}: ExportDialogProps) => {
  const { l10n } = useLocalization();
  const [fileName, setFileName] = useState<string>("");
  const [fileNameError, setFileNameError] = useState<string | null>(null);

  const localizeFileNameError = useCallback(
    (errorKey: FileNameErrorKey | null): string | null => {
      if (!errorKey) return null;
      return l10n.getString(FILE_NAME_ERROR_KEYS[errorKey]);
    },
    [l10n]
  );

  useEffect(() => {
    if (isOpen) {
      const defaultName = generateDefaultFileName(
        selectedFormat,
        savedViewName
      );
      setFileName(defaultName);
      setFileNameError(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen && fileName) {
      const updated = updateFileExtension(fileName, selectedFormat);
      setFileName(updated);
    }
  }, [selectedFormat]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      setFileName(newName);
      const error = validateFileName(newName);
      setFileNameError(localizeFileNameError(error));
    },
    [localizeFileNameError]
  );

  const handleFormatChange = useCallback(
    (value: string) => {
      onFormatChange(value as ExportFormat);
    },
    [onFormatChange]
  );

  const handleDownload = useCallback(() => {
    const error = validateFileName(fileName);
    if (error) {
      setFileNameError(localizeFileNameError(error));
      return;
    }
    const normalizedFileName = updateFileExtension(fileName, selectedFormat);
    onDownload(normalizedFileName);
  }, [fileName, selectedFormat, onDownload, localizeFileNameError]);

  const isDownloadDisabled =
    fileNameError !== null || !fileName.trim() || isExporting;

  return (
    <Dialog
      isOpen={isOpen}
      title={l10n.getString("transaction-export-dialog-title")}
      onUserDismiss={onClose}
      testId="export-dialog"
      footer={
        <div>
          <Row alignItems="center" justifyContent="end" gapSize="xs">
            <Row.Item shrink>
              <div className={styles.cancelButton}>
                <Button
                  kind="negative"
                  onClick={onClose}
                  label={l10n.getString("transaction-export-dialog-cancel")}
                  testId="export-dialog-cancel"
                />
              </div>
            </Row.Item>
            <Row.Item shrink>
              <Button
                kind="primary"
                onClick={handleDownload}
                label={l10n.getString("transaction-export-dialog-download")}
                startIcon="download"
                disabled={isDownloadDisabled}
                testId="export-dialog-download"
              />
            </Row.Item>
          </Row>
        </div>
      }
    >
      <p className="fontSize--s fontColor--darkGrey margin--bottom--m margin--top--m">
        {l10n.getString("transaction-export-dialog-subtitle")}
      </p>
      <div className="margin--bottom--m">
        <Radio
          kind="input-card"
          name="exportFormat"
          value="bai2"
          checked={selectedFormat === "bai2"}
          onCheck={() => handleFormatChange("bai2")}
        >
          <div className={styles.formatOption}>
            <span className="fontWeight--semibold">
              {l10n.getString("transaction-export-dialog-format-bai2")}
            </span>
            <Chip
              kind="success"
              label={l10n.getString("transaction-export-dialog-recommended")}
            />
          </div>
          <p className={styles.formatDescription}>
            {l10n.getString("transaction-export-dialog-bai2-description")}
          </p>
        </Radio>
      </div>
      <div className="margin--bottom--l">
        <Radio
          kind="input-card"
          name="exportFormat"
          value="csv"
          checked={selectedFormat === "csv"}
          onCheck={() => handleFormatChange("csv")}
        >
          <span className="fontWeight--semibold">
            {l10n.getString("transaction-export-dialog-format-csv")}
          </span>
          <p className={styles.formatDescription}>
            {l10n.getString("transaction-export-dialog-csv-description")}
          </p>
        </Radio>
      </div>
      <TextInput
        label={l10n.getString("transaction-export-dialog-filename-label")}
        value={fileName}
        onChange={handleFileNameChange}
        error={fileNameError}
      />
    </Dialog>
  );
};

export default ExportDialog;
